import React, { useEffect, useMemo, useState } from 'react';
import Card from '../Card/Card';
import Const from 'const.json';
import ChartPie from '../Charts/ChartPie/ChartPie';
import { useYearFromRedux } from 'utils/utilsLogic';
import { useAppSelector } from '../../redux/hooks';
import { EntityComplaint } from 'types/EntityComplaint';
import ListItemWithIcon from 'components/controls/ListItemWithIcon/ListItemWithIcon';
import { GenderEnum } from 'enums/enums';
import styles from 'assets/styles/components/DistributionByGender.module.scss';
import useApi from 'hooks/useApi';
import { GetComplaintsByGender } from 'api/apiService';
import WithApi from 'components/ui/WithApi/WithApi';

import maleImage from '../../assets/images/male.png';
import maleContrastImage from '../../assets/images/male-contrast.png';
import femaleImage from '../../assets/images/female.png';
import femaleContrastImage from '../../assets/images/female-contrast.png';

const DistributionOfComplaintsByGender: React.FC = () => {
  const { period, isContrast } = useAppSelector((state) => state.general);
  const { comparisonTextToLastYear } = useYearFromRedux();
  const { callApi, data, loading, error } = useApi<EntityComplaint[]>(GetComplaintsByGender);
  const [selectedIndexes, setSelectedIndexes] = useState<boolean[]>([]);

  const [logoDictionary, setLogoDictionary] = useState(new Map<string, string>([
    [GenderEnum.Male, isContrast ? maleContrastImage : maleImage],
    [GenderEnum.Female, isContrast ? femaleContrastImage : femaleImage]
  ]));

  const [colorDictionary, setColorDictionary] = useState(new Map<string, string>([
    [GenderEnum.Male, isContrast ? '#ADD8E6' : "#394491"],
    [GenderEnum.Female, isContrast ? '#FFC0CB' : "#67d9eb"]
  ]));

  useEffect(() => {
    setLogoDictionary(new Map<string, string>([
      [GenderEnum.Male, isContrast ? maleContrastImage : maleImage],
      [GenderEnum.Female, isContrast ? femaleContrastImage : femaleImage]
    ]));

    setColorDictionary(new Map<string, string>([
      [GenderEnum.Male, isContrast ? '#ADD8E6' : "#394491"],
      [GenderEnum.Female, isContrast ? '#FFC0CB' : "#67d9eb"]
    ]));
  }, [isContrast]);

  useEffect(() => {
    callApi();
  }, [period]);

  useEffect(() => {
    if (data) {
      data.forEach(d => d.logo = logoDictionary.get(d.id));
      setSelectedIndexes(data.map(() => true));
    }
  }, [data, logoDictionary]);

  const [pieData, labels, dataSetColors] = useMemo(() => {
    let selectedData = [];
    let pieData = [];
    let labels = [];
    let dataSetColors = [];
    if (data) {
      selectedData = data.filter((d, index) => selectedIndexes[index]) ?? [];
      pieData = selectedData.map(d => d.complaintsCount);
      labels = selectedData.map(d => d.description);
      dataSetColors = selectedData.map(d => colorDictionary.get(d.id));
    }
    return [pieData, labels, dataSetColors];
  }, [selectedIndexes, logoDictionary, colorDictionary, data]);

  return (
    <div>
      <Card
        header={Const.distributionOfComplaintsByGender}
        subHeader={comparisonTextToLastYear}
        components={[
          <WithApi data={data} loading={loading} error={error}>
            <div className={`d-md-flex align-items-center ${styles.genderContainer}`}>
              <div className='col-12 genderChart'>
                <ChartPie
                  details={{ data: pieData, dataSetColors, labels, title: Const.complaints }}
                  rotation={-90}
                  circumference={180}
                  ariaLabel={`${Const.distributionOfComplaintsByGender}, ${pieData[0] + pieData[1]} ${Const.complaints}`}
                />
              </div>
              <div className='col-12 d-flex justify-content-around'>
                {data?.map((item: EntityComplaint, index) => (
                  <ListItemWithIcon
                    index={index}
                    complaint={item}
                    key={item.id}
                    selectedIndexes={selectedIndexes}
                    setSelectedIndexes={setSelectedIndexes}
                    isDirectionText={true}
                    ariaLabelText={item.complaintsCount.toString()}
                  />
                ))}
              </div>
            </div>
          </WithApi>
        ]}
      />
    </div>
  );
};

export default DistributionOfComplaintsByGender;
